import { showErrorLayout } from 'widgets/toolbox/util';
import { debounce } from 'widgets/toolbox/debounce';
import { submitFormJsonWithAbort, TControlledRequest } from 'widgets/toolbox/ajax';
import { RefElement } from 'widgets/toolbox/RefElement';

import { TProduct as BaseProduct, TAvailabilityModel as TAvailabilityModel } from 'types/models/product';
import { TProductVariantSelectInstance } from './ProductVariantSelect';

type TProduct = BaseProduct & {
    price: BaseProduct['price'] & { html: string };
    stock: null | number;
    availability: TAvailabilityModel;
};

export type TUpdateProductResponse = {
    product: TProduct;
};

type TChangeAttributeRequest = TControlledRequest<TUpdateProductResponse>;

/**
 * @description ProductVariationChangeMixin implementation
 * @param ProductDetail Base widget for extending
 * @returns widget which triggers Bonus Product functionality
 */
function ProductVariationChangeMixinClassCreator(ProductDetail: import('widgets/product/ProductDetail').TProductDetail) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductVariationChangeMixin
     * @augments ProductDetail
     * @classdesc Represents ProductVariationChangeMixin component with next features:
     * 1. Handle variation change
     * 2. Updates variation view after it is selected/deselected
     * 3. Updates products view
     */
    class ProductVariationChangeMixin extends ProductDetail {
        changeAttributeRequests: Array<TChangeAttributeRequest> = [];

        selectedVariation: null | RefElement = null;

        changeAttributeID?: string;

        productOptionsSelect?: () => void;

        /**
         * @description trigger change from different widgets (swatch, select)
         * Saves `selectWidget.id` into a property for further global notifications etc.
         * Later, when triggering event, `selectWidget.id` will be analyzed in order to send with event correct data.
         * @param selectWidget widget
         * @returns Promise object represents server response with attribute changing result
         */
        changeAttribute(selectWidget: TProductVariantSelectInstance) {
            this.changeAttributeID = selectWidget.id;
            const selected = selectWidget.getSelectedVariations();

            if (!selected || selected.data('attrIsSelected')) {
                return Promise.resolve(null);
            }

            const requestObject = submitFormJsonWithAbort<TUpdateProductResponse>(
                this.getAttributeChangingURL(selected),
                undefined,
                'GET',
                true
            );

            this.changeAttributeRequests.push(requestObject);

            selectWidget.ref('content')
                .toggleClass(this.prefs().classesLoading, true)
                .attr('aria-busy', 'true');

            return requestObject.promise
                .then((response: Awaited<typeof requestObject.promise>) => {
                    if (!response) {
                        return null; // Aborted request
                    }

                    if (!response.success) {
                        this.showCartMessage(response.errorMessage, true);

                        return null;
                    }

                    this.afterAttributeChange(response);

                    return response;
                })
                .catch(error => {
                    showErrorLayout(error);

                    return null;
                })
                .finally(() => {
                    const index = this.changeAttributeRequests.indexOf(requestObject);

                    selectWidget.ref('content').toggleClass(this.prefs().classesLoading, false);

                    if (index > -1) {
                        this.changeAttributeRequests.splice(index, 1);
                    }
                });
        }

        /**
         * @description Get product attribute updating URL
         * @param selectedAttribute Selected attribute element
         */
        getAttributeChangingURL(selectedAttribute: RefElement): string {
            return String(selectedAttribute.data('attrUrl'));
        }

        /**
         * @description After change attribute handler
         * @param response response object
         * @param response.product - product
         */
        afterAttributeChange(response: TUpdateProductResponse): void {
            super.afterAttributeChange(response);

            if (response && response.product) {
                if (!this.prefs().disableHistory) {
                    this.updateHistoryState(response.product);
                }

                this.updateProductView(response.product);
                this.triggerChangeAttributeEvent(response);
            }
        }

        /**
         * @description Triggers global event after `afterAttributeChange` method executed.
         * Puts localized global alert event description
         * @param response response object
         */
        triggerChangeAttributeEvent(response: TUpdateProductResponse): void {
            if (!this.changeAttributeID) {
                return;
            }

            let accessibilityAlert = '';

            if (this.changeAttributeID.indexOf('quantity') === 0) {
                accessibilityAlert = this.prefs().accessibilityAlerts.quantitychanged;
                this.showAlert(accessibilityAlert, response);
            } else if (this.changeAttributeID.indexOf('attr-') === 0) {
                accessibilityAlert = this.prefs().accessibilityAlerts.variationselected;
                this.showAlert(accessibilityAlert, response);
            } else if (this.changeAttributeID.indexOf('option-') === 0) {
                accessibilityAlert = this.prefs().accessibilityAlerts.optionselected;

                // since Options changes applied immediately we need to delay success
                // message to not bother keyboard users

                if (!this.productOptionsSelect) {
                    this.productOptionsSelect = debounce(() => {
                        this.showAlert(accessibilityAlert, response);
                    }, 2000, false, true);
                }

                this.productOptionsSelect();
            }
        }

        /**
         * @description Update Product View
         * @param product productModel
         */
        updateProductView(product: TProduct): void {
            this.renderVariationAttributes(product);
            super.updateProductView(product);
        }

        /**
         * @description Render product attributes
         * @param product Product object
         */
        renderVariationAttributes(product: TProduct): void {
            if (product.variationAttributes && product.variationAttributes.length) {
                product.variationAttributes.forEach((variationAttribute) => {
                    variationAttribute.values = variationAttribute.values.map((value) => {
                        return {
                            ...value,
                            selected: value.selected,
                            disabled: !value.selectable
                        };
                    });
                    this.getById('attr-' + variationAttribute.attributeId, (attribute) => {
                        attribute.render('template', {
                            attr: variationAttribute
                        }, attribute.ref('container'));
                    });
                });
            }
        }

        /**
         * @description Update History State
         * @param product product object
         */
        updateHistoryState(product: TProduct): void {
            window.history.replaceState(undefined, '', product.selectedProductUrl);
        }
    }

    return ProductVariationChangeMixin;
}

export type TProductVariationChangeMixin = ReturnType<typeof ProductVariationChangeMixinClassCreator>;

export type TProductVariationChangeMixinInstance = InstanceType<TProductVariationChangeMixin>;

export default ProductVariationChangeMixinClassCreator;
